import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import Tag from '@atoms/Tag/Tag';
import { IMeta } from '@models/IMeta';
import {
  TabbedFlipboardCardType,
  TabbedFlipboardMediaCardType,
  TabbedFlipboardSplitCardType,
  TabbedFlipboardTextCardType,
} from '@models/ITabbedFlipboard';
import TabbedFlipboardCardTypeEnum from '@models/enums/TabbedFlipboardCardTypeEnum';
import TagTypesEnum from '@models/enums/TagTypesEnum';
import updateDataLayer from '@utils/helpers/gtm';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import TabbedFlipboardTab from './TabbedFlipboardTab';

interface ITabbedFlipboardTab {
  readonly title: string;
  readonly items: (
    | TabbedFlipboardMediaCardType
    | TabbedFlipboardTextCardType
    | TabbedFlipboardSplitCardType
  )[];
  readonly meta?: IMeta;
}

interface Props {
  readonly title: string;
  readonly subText?: string;
  readonly tabs: ITabbedFlipboardTab[];
  readonly meta?: IMeta;
}

const TabbedFlipboard = ({ title, subText, tabs, meta }: Props) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const isMobile = useMediaQuery(1024);

  const activeTab = tabs[activeTabIndex];
  const defaultTabItems = activeTab?.items || [];
  const tabItems = isMobile
    ? defaultTabItems.reduce<TabbedFlipboardCardType[]>((acc, item) => {
        if (item.type === TabbedFlipboardCardTypeEnum.Split) {
          return [...acc, item.topCard, item.bottomCard];
        }
        return [...acc, item];
      }, [])
    : defaultTabItems;

  const tabsTitles =
    tabs?.filter((tab) => tab.items.length > 0).map((tab) => tab.title) ?? [];
  const tabsContainer = (
    <div data-testid="CT-159-Tabbed-Flipboard-Tabs" className="min-w-0">
      <ShadowContainerHorizontal threshold={0.01} hasTabbableNestedItems>
        <div className="flex gap-x-5">
          {tabsTitles.map((tabTitle, index) => {
            const isActive = index === activeTabIndex;

            return (
              <Tag
                key={tabTitle}
                text={tabTitle}
                tagType={
                  isActive ? TagTypesEnum.Secondary : TagTypesEnum.Primary
                }
                pointerStates={!isActive}
                disabled={isActive}
                onClick={() => {
                  setActiveTabIndex(index);

                  updateDataLayer({
                    event: 'carouselSelect',
                    content_type: 'carousel_select',
                    link_text: tabTitle,
                  });
                }}
              />
            );
          })}
        </div>
      </ShadowContainerHorizontal>
    </div>
  );

  return (
    <section
      className="component-padding bg-white text-black-100 dark:bg-black-100 dark:text-white"
      data-testid="CT-159-Tabbed-Flipboard"
    >
      <div className="container">
        <h2
          className={classNames('mb-7.5 lg:w-3/4 xl:w-1/2', {
            'lg:mb-16': !subText,
          })}
          data-testid="CT-159-Tabbed-Flipboard-Title"
          data-epi-property-name={meta?.editHints?.title}
        >
          {title}
        </h2>

        {subText && (
          <p
            className={classNames(
              'mb-7.5 lg:mb-10 lg:w-3/4 xl:w-1/2',
              isMobile ? 'body-two' : 'h6'
            )}
            data-testid="CT-159-Tabbed-Flipboard-SubText"
            data-epi-property-name={meta?.editHints?.subText}
          >
            {subText}
          </p>
        )}
      </div>

      {tabsTitles.length > 0 && (
        <div data-epi-property-name={meta?.editHints?.tabs}>
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{
                opacity: 0,
                transition: {
                  duration: 0,
                },
              }}
              key={`${tabsTitles[activeTabIndex]}-${activeTabIndex}`}
            >
              <TabbedFlipboardTab
                carouselId={`tabbed-flipboard-carousel-${
                  meta?.contentLink ?? ''
                }`}
                hasMultipleTabs={tabsTitles.length > 1}
                items={tabItems}
                tabsContainer={tabsContainer}
              />
            </motion.div>
          </AnimatePresence>
        </div>
      )}
    </section>
  );
};

export default TabbedFlipboard;
